
/* ============================== src/components/Modals/EmployeeCardModal.jsx ============================== */
// path: frontend/src/components/Modals/EmployeeCardModal.jsx
/**
 * Read-only карточка сотрудника.
 *
 * Активные замещения (в которых сотрудник замещающий) приходят в
 * employee.substitutions с GET /employees — бэкенд считает их батчем в
 * EmployeeRepo.loadActiveSubstitutions. Раньше собирались на клиенте
 * через getSubstitutionInfo — удалено.
 *
 * Изменения в 2b-2d:
 *   - источник substitutions: employee.substitutions;
 *   - fromEmpId → fromId (поле SubstitutionDto на бэкенде);
 *   - reasonText/delegateTasks приходят прямо в SubstitutionDto.
 */
import { Fragment } from "react";
import { createPortal } from "react-dom";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { substitutionKindLabel } from "../../utils/constants";
import { buildEmployeePositions } from "../../utils/helpers";
import { fmtDMY } from "../../utils/date";
import { useNestedModalEscape } from "../Templates/useNestedModalEscape";

const NOOP = () => {};

export const EmployeeCardModal = ({ employee, db, onClose }) => {
  useNestedModalEscape(onClose);

  if (!employee) return null;

  const activeSubs = [...(employee.substitutions ?? [])].sort((a, b) =>
    a.start < b.start ? -1 : a.start > b.start ? 1 : 0,
  );

  const positions = buildEmployeePositions(employee, db);
  const primary = positions.find((p) => p.kind === "primary") || null;
  const extras = positions.filter((p) => p.kind === "extra");

  const emailFull = employee.email || "";

  const substitutionRows = activeSubs.map((sub) => {
    const fromEmp = db?.employees?.find((e) => e.id === sub.fromId);
    const fromName = fromEmp ? `${fromEmp.last} ${fromEmp.first}` : sub.fromId;
    const periodLabel = sub.end
      ? `${fmtDMY(sub.start)} – ${fmtDMY(sub.end)}`
      : `${fmtDMY(sub.start)} – до отмены`;
    return {
      id: sub.id,
      fromName,
      kindLabel: substitutionKindLabel(sub.kind),
      periodLabel,
      tasksLabel: sub.delegateTasks ? "переданы" : "только роли",
    };
  });

  const substitutionBlock =
    substitutionRows.length > 0 ? (
      <>
        <div className="rep-panel-title">Замещение и делегирование</div>
        {substitutionRows.map((row) => (
          <Fragment key={row.id}>
            <FormField
              label="Замещает"
              value={row.fromName}
              onChange={NOOP}
              disabled
              inline
            />
            <FormField
              label="Повод"
              value={row.kindLabel}
              onChange={NOOP}
              disabled
              inline
            />
            <FormField
              label="Период"
              value={row.periodLabel}
              onChange={NOOP}
              disabled
              inline
            />
            <FormField
              label="Задачи"
              value={row.tasksLabel}
              onChange={NOOP}
              disabled
              inline
            />
          </Fragment>
        ))}
      </>
    ) : null;

  const footer = (
    <div className="modal-foot">
      <div className="spacer" />
      <button type="button" className="btn ghost" onClick={onClose}>
        Закрыть
      </button>
    </div>
  );

  const modal = (
    <ModalShell
      title="Карточка сотрудника"
      subtitle={employee.fired ? "Уволен" : null}
      onClose={onClose}
      width={560}
      footer={footer}
      showSave={false}
    >
      <div className="project-info-fields">
        <FormField
          label="Фамилия"
          value={employee.last || "-"}
          onChange={NOOP}
          disabled
          inline
        />
        <FormField
          label="Имя"
          value={employee.first || "-"}
          onChange={NOOP}
          disabled
          inline
        />
        <FormField
          label="Основная должность"
          value={primary?.position || employee.position || "-"}
          onChange={NOOP}
          disabled
          inline
        />
        <FormField
          label="Основное подразделение"
          value={primary?.orgName || "-"}
          onChange={NOOP}
          disabled
          inline
        />

        {extras.map((p, i) => {
          const suffix = i === 0 ? "" : ` ${i + 1}`;
          const rateBadge =
            p.rate != null && p.rate !== 1 ? (
              <span className="pos-tag">{p.rate} ст.</span>
            ) : null;
          return (
            <Fragment key={i}>
              <FormField
                label={`Должность совм${suffix}`}
                value={p.position || "-"}
                onChange={NOOP}
                disabled
                inline
                below={rateBadge}
              />
              <FormField
                label={`Подразделение совм${suffix}`}
                value={p.orgName || "-"}
                onChange={NOOP}
                disabled
                inline
              />
            </Fragment>
          );
        })}

        <FormField
          label="Внутренний телефон"
          value={employee.extension || "-"}
          onChange={NOOP}
          disabled
          inline
        />
        <FormField
          label="E-mail"
          value={emailFull || "-"}
          onChange={NOOP}
          disabled
          inline
        />

        {substitutionBlock}
      </div>
    </ModalShell>
  );

  return createPortal(
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {modal}
    </div>,
    document.body,
  );
};

export default EmployeeCardModal;
